import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import clsx from 'clsx';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import omit from 'lodash/omit';
import {
  Button,
} from '@populateui/ui';
import {
  EOBExtractionResponseDTO,
  EOBWithInsurancePaymentResponseDTO,
} from 'dtos';
import {
  EOBExtractionJobStatus,
} from 'dtos/eOBExtractionJobStatus';
import {
  FormProvider,
  useForm,
} from 'react-hook-form';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import {
  notEmptyString,
} from '@populateui/utils';
import Dialog from 'pages/Dashboard/components/Dialog';
import Controller from 'core/components/FormController';
import Input from 'pages/Dashboard/components/Input';
import InsuranceLookup from 'pages/Dashboard/components/InsuranceLookup';
import MediaPreview from 'pages/Dashboard/components/MediaPreview';
import PanelExtension from 'core/components/PanelExtension';
import ToolTip from 'core/components/Tooltip';
import Spinner from 'pages/Dashboard/components/Spinner';
import {
  useNotify,
} from 'core/hooks';
import useReconcileExtractedEOB from 'pages/Dashboard/pages/Billing/Payments/EOBDialog/ReconcileDialog/hooks';
import ServiceLineDiscrepanciesSection from 'pages/Dashboard/pages/Billing/Payments/EOBDialog/ReconcileDialog/ServiceLineDiscrepanciesSection';
import UnmatchedClaimsSection from 'pages/Dashboard/pages/Billing/Payments/EOBDialog/ReconcileDialog/UnmatchedClaimsSection';
import {
  MultimediaType,
} from 'pages/Dashboard/utils/constants';
import EyeIcon from 'core/icons/eye';

type FormValues = {
  correctedPayerName: string;
  claimInputs: Record<string, string>;
};

type ParsedEOBErrors = Omit<EOBExtractionResponseDTO, 'eobWithInsurancePayment'>;

type Props = {
  eobExtractionJobId: number;
  eobNumber?: string;
  parsedEOBData: EOBWithInsurancePaymentResponseDTO;
  parsedEOBErrors: ParsedEOBErrors;
  parsedEOBFile?: File;
  onContinue: (
    updatedData: EOBWithInsurancePaymentResponseDTO,
    updatedErrors: ParsedEOBErrors,
  ) => void;
  onClose: () => void;
};

export default function ReconcileDialog({
  eobExtractionJobId,
  eobNumber,
  parsedEOBData,
  parsedEOBErrors,
  parsedEOBFile,
  onContinue,
  onClose,
}: Props) {
  const notify = useNotify();
  const { mutateAsync: reconcile, isPending } = useReconcileExtractedEOB();

  const [localErrors, setLocalErrors] = useState(parsedEOBErrors);
  const [localParsedEOBData, setLocalParsedEOBData] = useState(parsedEOBData);
  const [isPreviewVisible, setIsPreviewVisible] = useState(false);
  const [parsedEOBPreviewUrl, setParsedEOBPreviewUrl] = useState<string | null>(null);

  const formProps = useForm<FormValues>({
    defaultValues: {
      correctedPayerName: '',
      claimInputs: {},
    },
  });

  const { control, handleSubmit, reset, watch } = formProps;

  const unmatchedClaims = localErrors.claimsPresentInEobButMissingInSystem ?? [];
  const isPayerMissing = localErrors.isPayerMissingInSystem === true;
  const unmatchedCount = unmatchedClaims.length + (isPayerMissing ? 1 : 0);

  const serviceLineEobMissingInClaim = localErrors.serviceLinesPresentInEobButMissingInClaim ?? [];
  const serviceLineClaimMissingInEob = localErrors.serviceLinesPresentInClaimButMissingInEob ?? [];
  const hasServiceLineDiscrepancies = serviceLineEobMissingInClaim.length > 0
    || serviceLineClaimMissingInEob.length > 0;

  const [watchedPayerName, watchedClaimInputs] = watch(['correctedPayerName', 'claimInputs']);
  const hasPayerCorrection = isPayerMissing && notEmptyString(watchedPayerName);
  const hasClaimCorrection = Object.values(watchedClaimInputs ?? {})
    .some((value) => notEmptyString(value));
  const canReconcile = hasPayerCorrection || hasClaimCorrection;

  useEffect(() => {
    const nextUrl = isNil(parsedEOBFile) ? null : URL.createObjectURL(parsedEOBFile);
    setParsedEOBPreviewUrl(nextUrl);

    return () => {
      if (!isNil(nextUrl)) {
        URL.revokeObjectURL(nextUrl);
      }
    };
  }, [parsedEOBFile]);

  const parsedEOBPreviewMedia = useMemo(() => {
    if (isNil(parsedEOBFile) || isNil(parsedEOBPreviewUrl)) {
      return null;
    }

    return {
      original: parsedEOBFile,
      media: {
        url: { value: parsedEOBPreviewUrl },
        fileName: parsedEOBFile.name,
      },
    };
  }, [parsedEOBFile, parsedEOBPreviewUrl]);
  const hasPreviewMedia = !isNil(parsedEOBPreviewMedia);
  const previewRightPadding = isPreviewVisible ? 'pr-[28rem]' : 'pr-2';

  const handleReconcile = handleSubmit(async (values) => {
    try {
      const claimCorrections = Object.entries(values.claimInputs)
        .filter(([, value]) => !isEmpty(value))
        .map(([extracted, corrected]) => ({
          extractedClaimExternalId: extracted,
          correctedClaimExternalId: corrected,
        }));
      const correctedPayerName = notEmptyString(values.correctedPayerName)
        ? values.correctedPayerName : undefined;

      const result = await reconcile({
        eobExtractionJobId,
        correctedPayerName: isPayerMissing ? correctedPayerName : undefined,
        claimCorrections: claimCorrections.length > 0 ? claimCorrections : undefined,
      });

      if (result.status !== EOBExtractionJobStatus.Failed) {
        const updatedErrors = omit(
          result.result ?? {},
          'eobWithInsurancePayment',
        ) as ParsedEOBErrors;
        const updatedData = result.result?.eobWithInsurancePayment ?? localParsedEOBData;

        setLocalErrors(updatedErrors);
        setLocalParsedEOBData(updatedData);
        reset({
          correctedPayerName: updatedData.eob?.insuranceName ?? '',
          claimInputs: {},
        });

        const stillUnmatched = updatedErrors.claimsPresentInEobButMissingInSystem?.length ?? 0;
        const claimErrorLabel = stillUnmatched > 0 ? `${stillUnmatched} claim(s) still unmatched after reconciliation.` : null;
        const payerErrorLabel = updatedErrors.isPayerMissingInSystem ? 'Payer is still missing after reconciliation.' : null;
        if (!isNil(claimErrorLabel) || !isNil(payerErrorLabel)) {
          notify({
            message: (
              <div>
                {!isNil(claimErrorLabel) && <p>{claimErrorLabel}</p>}
                {!isNil(payerErrorLabel) && <p>{payerErrorLabel}</p>}
              </div>
            ),
            variant: 'warning',
          });
        } else {
          onContinue(updatedData, updatedErrors);
        }
      }
    } catch {
      console.error('Reconciliation failed');
    }
  });

  const handleProceedAnyway = useCallback(() => {
    onContinue(localParsedEOBData, localErrors);
  }, [localErrors, localParsedEOBData, onContinue]);

  const previewToggleLabel = isPreviewVisible ? 'Hide EOB' : 'Show EOB';

  return (
    <Dialog
      open
      title={(
        <div className="w-full flex items-start justify-between gap-3">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Review Extracted Data</h3>
            <p className="text-xs text-gray-500">
              Verify and correct AI-extracted values before proceeding.
            </p>
          </div>
          {!isNil(parsedEOBFile) && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => { setIsPreviewVisible((current) => !current); }}
              className="!h-6 shrink-0 !-mt-2 -mr-1"
            >
              <EyeIcon
                className={clsx('!w-4 !h-4 mr-2 !text-blue-500')}
              />
              {previewToggleLabel}
            </Button>
          )}
        </div>
      )}
      onClose={onClose}
      disableBackdropClick={isPending}
      disableEscapeKeyDown={isPending}
      className={clsx(
        'text-xs !p-0 !space-y-0 reconcile-dialog transition-[width] duration-150 ease-in-out',
        {
          '!w-[72rem]': isPreviewVisible,
          '!w-[44rem]': !isPreviewVisible,
        },
      )}
      titleClassName="!p-5"
    >
      <div className="relative flex h-[42rem] max-h-[calc(100vh-2rem)] w-full min-h-0 overflow-hidden">
        <FormProvider {...formProps}>
          <div className={clsx(
            'flex h-full min-h-0 w-full max-w-full shrink-0 flex-col gap-2 px-2',
            previewRightPadding,
          )}
          >
            <div className="flex items-center gap-2 flex-wrap px-5 py-3 border-y border-gray-100">
              <span className="rounded-full font-medium border border-gray-300 bg-gray-50 px-2 py-0.5 text-xs text-gray-700">
                {!isNil(eobNumber) && !isEmpty(eobNumber) && ` · EOB #${eobNumber}`}
              </span>
              {unmatchedCount > 0 && (
                <span className="flex items-center gap-1 rounded-full border border-orange-200 bg-orange-50 px-2 py-0.5 text-xs font-medium text-orange-600">
                  <ErrorOutlineOutlinedIcon className="!h-4 !w-4" />
                  {unmatchedCount}
                  {' '}
                  unmatched
                </span>
              )}
            </div>
            <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-5 pb-4">
              <div className="flex flex-col gap-4">
                <div className={clsx(
                  'rounded-lg border p-2.5 flex flex-col gap-2',
                  isPayerMissing ? 'border-orange-100 bg-[#FAEEDA22]' : 'border-gray-200 bg-white',
                )}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-gray-900">PAYER</span>
                    {!isPayerMissing && (
                      <span className="flex items-center gap-1 rounded-full border border-green-200 bg-green-50 px-1.5 py-0.5 text-[10px] font-medium text-green-600">
                        <CheckCircleOutlinedIcon className="!h-3 !w-3" />
                        Matched
                      </span>
                    )}
                  </div>
                  <div className="reconcile-dialog-field-row">
                    <Input
                      id="ai-extracted-payer"
                      label="AI Extracted"
                      value={localParsedEOBData.eob?.insuranceName ?? ''}
                      disabled
                      className="reconcile-dialog-input"
                    />
                    <span className="pb-1.5 text-xs text-gray-300">→</span>
                    <Controller
                      control={control}
                      name="correctedPayerName"
                      render={({ field }) => (
                        isPayerMissing ? (
                          <InsuranceLookup
                            {...field}
                            id="reconcile-payer-lookup"
                            onChange={(payer) => { field.onChange(payer?.payerName ?? ''); }}
                            label="Payer in System"
                            className="reconcile-dialog-lookup-input"
                          />
                        ) : (
                          <Input
                            {...field}
                            id="matched-payer"
                            label="Payer in System"
                            disabled
                            className="reconcile-dialog-input"
                          />
                        )
                      )}
                    />
                  </div>
                </div>
                <UnmatchedClaimsSection unmatchedClaims={unmatchedClaims} />
                <ServiceLineDiscrepanciesSection
                  hasServiceLineDiscrepancies={hasServiceLineDiscrepancies}
                  serviceLineEobMissingInClaim={serviceLineEobMissingInClaim}
                  serviceLineClaimMissingInEob={serviceLineClaimMissingInEob}
                />
              </div>
            </div>
            <div className="px-5 py-3 flex items-center justify-between border-t border-gray-100 shrink-0">
              <Button
                variant="outlined"
                size="small"
                onClick={onClose}
                disabled={isPending}
                className="!text-gray-800 !border-gray-300 !h-8 !px-4"
              >
                Close
              </Button>
              <div className="flex items-center gap-4">
                <Button
                  variant="outlined"
                  onClick={handleProceedAnyway}
                  disabled={isPending}
                  className="!h-8"
                >
                  Proceed Anyway
                </Button>
                <ToolTip
                  title="Correct at least one payer or claim to reconcile"
                  hide={canReconcile}
                >
                  <div>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleReconcile}
                      disabled={!canReconcile || isPending}
                      className="!h-8"
                    >
                      {isPending ? (
                        <div className="flex items-center gap-1">
                          <Spinner className="!text-inherit" />
                          Reconciling...
                        </div>
                      ) : 'Reconcile'}
                    </Button>
                  </div>
                </ToolTip>
              </div>
            </div>
          </div>
        </FormProvider>
        {hasPreviewMedia && isPreviewVisible && (
          <PanelExtension
            className="absolute right-0 pl-4 pb-4 mr-4 mb-4 top-0 h-full w-[26rem] rounded-none overflow-hidden bg-white"
          >
            <div className="h-full min-h-0 overflow-hidden bg-white">
              <MediaPreview
                media={parsedEOBPreviewMedia.media}
                mediaType={MultimediaType.EOB}
                className="h-full w-full"
                hasDelete={false}
                thumbnail
                fullSize
              />
            </div>
          </PanelExtension>
        )}
      </div>
    </Dialog>
  );
}
